import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { injectIntl, useIntl } from 'react-intl';
import DataCacheService from '@/shared/services/DataCacheService';
import enumCache from '@/shared/services/EnumCacheService';
import intlShape from '@/shared/utils/intlShape';
import availableVatPercentages from '../registration/vatPercentages';

const VatRateTypeSelector = ({ vatRate, vatType, onChange, disabled, className, intl }) => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    const cache = DataCacheService.getDataCache();
    if (!cache) {
      throw new Error('DataCache not found in VatRateTypeSelector.jsx');
    }

    const tenantConfiguration = cache.tenantConfiguration || 0;

    const percentages = availableVatPercentages.getByConfiguration(tenantConfiguration) || [];

    enumCache.getTranslated('vatType', intl).then(data => {
      const types = data.filter(x => x.id !== 0);
      setItems([
        ...percentages.map(x => ({
          id: `0;${x.label}`,
          label: x.label,
          vatType: 0,
          vatRate: x.value,
        })),
        ...types.map(x => ({
          id: `${x.id};0`,
          label: x.label,
          vatType: x.id,
          vatRate: 0.0,
        })),
      ]);
    });
  }, [intl]);

  const interalValue = useMemo(() => {
    if (vatType === null || vatRate === null) return null;

    const item = items.find(x => x.vatType === vatType && x.vatRate === vatRate);
    return item ? item.id : null;
  }, [vatRate, vatType, items]);
  const update = useCallback(
    e => {
      const { value = null } = e.target;
      if (!value) onChange({ vatRate: null, vatType: null });
      const item = items.find(x => x.id === value);
      if (item) {
        onChange({ vatRate: item.vatRate, vatType: item.vatType });
      }
    },
    [items, onChange],
  );

  return (
    <select
      value={interalValue || ''}
      className={classNames('form-control', className)}
      onChange={update}
      disabled={disabled}
    >
      {items.map(p => (
        <option value={p.id} key={p.id}>
          {p.label}
        </option>
      ))}
    </select>
  );
};
VatRateTypeSelector.propTypes = {
  vatRate: PropTypes.number,
  vatType: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  intl: intlShape.isRequired,
};
VatRateTypeSelector.defaultProps = {
  vatRate: null,
  vatType: null,
  disabled: false,
  className: '',
};

export default injectIntl(VatRateTypeSelector);
